import { differenceInDays, differenceInSeconds, format, parse, parseISO, setYear, addYears, isBefore } from 'date-fns';
import { formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz';

import { USAStatesByAbbv } from '@ourbranch/utility-types';
import { stateToTzMap } from '@ourbranch/state-to-tz-map';

// We default to Ohio for our state for timezone purposes
let tzSavedState = 'OH';

/** Coerce a date-ish value to a Date. Strings are parsed as ISO-8601 (local
 * midnight for date-only, matching the previous moment() behavior); Date/number/
 * anything with valueOf() (e.g. a moment instance from a not-yet-migrated caller)
 * go through the Date constructor. A nullish value yields an Invalid Date (NaN)
 * rather than silently resolving to the Unix epoch — matching moment(null). */
const toDateValue = (value: Date | string | number): Date => {
  if (value == null) {
    return new Date(NaN);
  }
  return typeof value === 'string' ? parseISO(value) : new Date(value);
};

/** Truncate toward zero (matches moment's absFloor used by moment.duration). */
const absFloor = (n: number): number => (n < 0 ? Math.ceil(n) : Math.floor(n));

/** Return today's date as a string, accounting for timezones. */
export function getTodayStr(stateIn?: USAStatesByAbbv) {
  const state = stateIn || tzSavedState; // we recall the last state if one isn't sent in
  tzSavedState = state;

  const now = new Date();
  if (['WA', 'CA', 'OR', 'NV'].includes(state)) {
    return formatInTimeZone(now, 'America/Los_Angeles', 'yyyy-MM-dd');
  }
  if (['AZ', 'NM', 'WY', 'UT', 'CO', 'MT', 'ID', 'ND', 'SD', 'NE'].includes(state)) {
    return formatInTimeZone(now, 'America/Boise', 'yyyy-MM-dd');
  }
  if (['AL', 'AR', 'IL', 'IA', 'LA', 'MN', 'MO', 'MS', 'OK', 'WI', 'IN', 'KS', 'MI', 'TN', 'TX'].includes(state)) {
    return formatInTimeZone(now, 'America/Chicago', 'yyyy-MM-dd');
  }

  if (state === 'HI') {
    return formatInTimeZone(now, 'Pacific/Honolulu', 'yyyy-MM-dd');
  }
  if (state === 'AK') {
    return formatInTimeZone(now, 'America/Anchorage', 'yyyy-MM-dd');
  }
  // default to eastern
  return formatInTimeZone(now, 'America/New_York', 'yyyy-MM-dd');
}

/** Returns number of days between passed date and birthdate */
export function daysBetweenDateAndNextBirthday(date: Date, birthDate: Date) {
  const thisYearBirthday = setYear(birthDate, new Date().getFullYear());

  if (isBefore(date, thisYearBirthday)) {
    return differenceInDays(thisYearBirthday, date);
  }

  return differenceInDays(addYears(thisYearBirthday, 1), date);
}

/**
 * Returns an object describing age given a Date object set to birthday.
 * Preserves moment.duration()'s 365.25-day-average year semantics (days →
 * months via 4800/146097, months → years via /12), which rating/underwriting
 * relies on — a calendar-accurate diff would shift ages near birthdays.
 */
export function calculateAge(birthday: Date | string, startMoment: Date | string | number = new Date()) {
  const days = absFloor((toDateValue(startMoment).getTime() - toDateValue(birthday).getTime()) / 86_400_000);
  const monthsTotal = absFloor((days * 4800) / 146097);
  const years = absFloor(monthsTotal / 12);
  return { years, months: monthsTotal - years * 12 };
}

export function getDateFromUnparsedString(dateString: string) {
  if (typeof dateString === 'string' && dateString.length === 8) {
    const year = dateString.substr(0, 4);
    const month = dateString.substr(4, 2);
    const day = dateString.substr(6, 2);
    return `${year}-${month}-${day}`;
  }
  return dateString;
}

/** Returns a boolean depending upon whether the supplied string is in the future */
export function isFutureDate(yyyymmddStr: string) {
  return parseInt(yyyymmddStr.replace(/[^0-9]/g, ''), 10) >= parseInt(format(new Date(), 'yyyyMMdd'), 10);
}

/** Returns the number of days between now and a supplied day.
 * `day` must be an ISO-8601 string — `parseISO` is strict and returns NaN for
 * other formats (unlike the previous lenient moment(day) parsing). */
export function daysBetween(day: string) {
  return differenceInDays(new Date(), parseISO(day)) - 1;
}

/** Returns the number of seconds between now and a supplied date/time.
 * A string `dt` must be ISO-8601 (parsed strictly via parseISO). */
export function secondsBetween(dt: Date | string | number) {
  return differenceInSeconds(new Date(), toDateValue(dt)) - 1;
}

/** Returns YYYY-MM-DD from YYYYMMDD */
export function yyyymmddToAWSDate(yyyymmddStrIn: string) {
  const yyyymmddStr = typeof yyyymmddStrIn === 'string' ? yyyymmddStrIn : String(yyyymmddStrIn);
  return yyyymmddStr.length === 8
    ? [yyyymmddStr.slice(0, 4), yyyymmddStr.slice(4, 6), yyyymmddStr.slice(6, 8)].join('-')
    : null;
}

/** Helper function that pads a string to two digits */
function padTwoDigits(str: string) {
  return String(`00${String(str)}`).slice(-2);
}

/** Returns YYYY-MM-DD from an Excel Date  */
export function ExcelDateToAWSDate(serial: number) {
  const utcDays = Math.floor(serial - 25569);
  const utcValue = utcDays * 86400;
  const dateInfo = new Date(utcValue * 1000);

  return `${dateInfo.getFullYear()}-${padTwoDigits(String(dateInfo.getMonth()))}-${padTwoDigits(
    String(dateInfo.getDate())
  )}`;
}

/** take a variety of different date formats and turn them all into YYYY-MM-DD */
export function normalizeDate(inputDate: string) {
  if (inputDate.length === 8 && !inputDate.includes('-')) {
    // assuming YYYYMMDD date
    return yyyymmddToAWSDate(inputDate);
  }
  if (inputDate.length < 8 && parseInt(inputDate, 10) > 0) {
    // assuming excel date:
    return ExcelDateToAWSDate(parseInt(inputDate, 10));
  }

  // return what we received
  return inputDate;
}

/**
 * Parse a date string that may be ISO ('yyyy-MM-dd' or a full ISO datetime) or US 'MM/dd/yyyy'
 * (e.g. vendor inspection dates). Returns a Date, or null when the value is missing/unparseable.
 * Timezone-naive — intended for date-only business values. (#33158)
 */
export function parseFlexibleDate(value?: string | null): Date | null {
  if (!value || typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();

  // ISO: 'yyyy-MM-dd' and full ISO datetimes.
  const iso = parseISO(trimmed);
  if (!Number.isNaN(iso.getTime())) {
    return iso;
  }

  // US-format 'MM/dd/yyyy'. Fixed reference date keeps parsing deterministic (no dependency on now).
  const us = parse(trimmed, 'MM/dd/yyyy', new Date(0));
  if (!Number.isNaN(us.getTime())) {
    return us;
  }

  return null;
}

/**
 * Return the latest of several flexible-format date strings (see {@link parseFlexibleDate}) as a
 * Date, or null when none parse. Callers format the result for their own needs. (#33158)
 */
export function pickLatestFlexibleDate(...values: Array<string | null | undefined>): Date | null {
  const dates = values.map(parseFlexibleDate).filter((d): d is Date => d !== null);
  if (!dates.length) {
    return null;
  }
  return dates.reduce((a, b) => (a.getTime() >= b.getTime() ? a : b));
}

export const localToUtcTime = (date: string | number | Date, state: string) => {
  return zonedTimeToUtc(date, stateToTzMap[state]);
};
